import { Text } from '@mantine/core';
import type { FieldKind } from '../../domain/fields.js';
import { SYNC_STATUS } from '../../domain/status.js';
import { EMPTY, NONE, NOT_SET, valueText } from '../../domain/valueText.js';

/** Which side of a change a value is: `before` is being replaced, `after` is what gets written. */
export type ValueTone = 'before' | 'after';

const TONE_COLOR: Record<ValueTone, string> = { before: SYNC_STATUS.drift.color, after: SYNC_STATUS.in_sync.color };

/**
 * One value as it reads in a diff: plain text off `valueText`, never JSON. A before value is
 * dimmed; `Not set` and `None` are always dimmed, because they say nothing is there. A `tone`
 * sits the value on its side's soft tint — drift for the value being replaced, in sync for the
 * value that replaces it — so the direction of a change reads without the headings.
 */
export const ValueText = ({
    value,
    kind,
    dimmed,
    tone,
}: {
    value: unknown;
    kind?: FieldKind;
    dimmed?: boolean;
    tone?: ValueTone;
}) => {
    const text = valueText(value, kind);
    const empty = text === NOT_SET || text === NONE || text === EMPTY;
    return (
        <Text
            fz="xs"
            c={dimmed || empty ? 'dimmed' : undefined}
            px={tone ? 6 : undefined}
            py={tone ? 1 : undefined}
            style={{
                wordBreak: 'break-word',
                ...(tone && {
                    display: 'inline-block',
                    maxWidth: '100%',
                    borderRadius: 4,
                    background: `var(--mantine-color-${TONE_COLOR[tone]}-light)`,
                }),
            }}
        >
            {text}
        </Text>
    );
};
